/* ----------------------------------------------------------------------------
 * Project B — Weekly Competitor Tracking data fetchers (Lane C, FE).
 *
 * Five GET reads under /v1/seo/projects/{id}/competitor-tracking/ (spec §5.3).
 * ALL FIVE ARE FREE, read-only aggregates over stored rows — they NEVER call a
 * provider and NEVER spend. Unlike the PAID SEO-B surfaces (which click-gate),
 * these are safe to fire with a plain useQuery on mount.
 *
 * Self-contained + OWNED by Lane C (kept out of the orchestrator-wired shared
 * data/sources.ts). Gated behind USE_MOCK_DATA, honest nulls via numOrNull /
 * strOrNull (never a fabricated zero). Shapes match the LOCKED §5.3 contract
 * exactly — no field is invented beyond it.
 * -------------------------------------------------------------------------- */

import { api, USE_MOCK_DATA } from '@/lib/api';
import type {
  TrackingCompetitor,
  TrackingCompetitors,
  TrackingDistribution,
  TrackingDistributionDomain,
  TrackingKeywordMap,
  TrackingKeywordMapRow,
  TrackingOwnStats,
  TrackingStatsBase,
  TrackingSummary,
  TrackingWeeks,
} from '@/types/seo-tracking';

/** Finite number or null — blank/absent/non-numeric all stay null. NEVER 0. */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Non-empty string or null — never coerces to a fabricated value. */
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Finite number or a 0 fallback — for COUNT fields the API always supplies. */
function numOr0(v: unknown): number {
  const n = numOrNull(v);
  return n === null ? 0 : n;
}

/** The shared stat block, mapped null-honest. */
function mapStatsBase(o: Record<string, unknown>): TrackingStatsBase {
  return {
    visibility_pct: numOrNull(o.visibility_pct),
    visibility_diff: numOrNull(o.visibility_diff),
    avg_position: numOrNull(o.avg_position),
    keyword_count: numOrNull(o.keyword_count),
    top3: numOrNull(o.top3),
    top10: numOrNull(o.top10),
    top20: numOrNull(o.top20),
    top100: numOrNull(o.top100),
    improved: numOrNull(o.improved),
    declined: numOrNull(o.declined),
    new_count: numOrNull(o.new_count),
    lost: numOrNull(o.lost),
  };
}

/* ===== MOCKS (dev only; USE_MOCK_DATA) ====================================== */

const MOCK_WEEKS: TrackingWeeks = {
  weeks: ['2026-08-03', '2026-07-27', '2026-07-20'],
  latest: '2026-08-03',
};

const MOCK_OWN: TrackingOwnStats = {
  visibility_pct: 0.97,
  visibility_diff: -0.18,
  avg_position: 65.6,
  avg_position_diff: 3.0,
  keyword_count: 7,
  top3: 0,
  top10: 0,
  top20: 0,
  top100: 7,
  improved: 3,
  declined: 4,
  new_count: 1,
  lost: 1,
};

const MOCK_SUMMARY: TrackingSummary = {
  week: '2026-08-03',
  prev_week: '2026-07-27',
  own_domain: 'omegahub.co.uk',
  tracked_keywords: 15,
  keywords_captured: 15,
  own: MOCK_OWN,
  source: 'dataforseo.serp.organic_live',
  captured_at: '2026-08-03 05:31:02',
};

const MOCK_COMPETITORS: TrackingCompetitor[] = [
  {
    domain: 'omegahub.co.uk', is_own: true, rank: 9, visibility_pct: 0.97, visibility_diff: -0.18,
    est_traffic: null, keyword_count: 7, avg_position: 65.6, position_stddev: 24.1,
    top3: 0, top10: 0, top20: 0, top100: 7, improved: 3, declined: 4, new_count: 1, lost: 1,
  },
  {
    domain: 'cntraveller.com', is_own: false, rank: 1, visibility_pct: 32.42, visibility_diff: 16.26,
    est_traffic: null, keyword_count: 9, avg_position: 13.6, position_stddev: 28.86,
    top3: 2, top10: 8, top20: 8, top100: 9, improved: 2, declined: 1, new_count: 2, lost: 1,
  },
  {
    domain: 'eventbrite.co.uk', is_own: false, rank: 2, visibility_pct: 21.1, visibility_diff: -2.4,
    est_traffic: null, keyword_count: 11, avg_position: 22.9, position_stddev: 19.4,
    top3: 1, top10: 5, top20: 7, top100: 11, improved: 4, declined: 5, new_count: 0, lost: 2,
  },
];

const MOCK_DISTRIBUTION: TrackingDistribution = {
  weeks: ['2026-07-27', '2026-08-03'],
  domains: [
    {
      domain: 'omegahub.co.uk', is_own: true,
      series: [
        { week: '2026-07-27', top3: 0, top10: 0, top20: 0, top100: 6, visibility_pct: 1.15 },
        { week: '2026-08-03', top3: 0, top10: 0, top20: 0, top100: 7, visibility_pct: 0.97 },
      ],
    },
    {
      domain: 'cntraveller.com', is_own: false,
      series: [
        { week: '2026-07-27', top3: 1, top10: 6, top20: 8, top100: 9, visibility_pct: 16.16 },
        { week: '2026-08-03', top3: 2, top10: 8, top20: 8, top100: 9, visibility_pct: 32.42 },
      ],
    },
  ],
};

const MOCK_KEYWORD_MAP: TrackingKeywordMap = {
  week: '2026-08-03',
  prev_week: '2026-07-27',
  keywords: [
    {
      keyword_id: 12, keyword: 'sound bath',
      own: { position: 45, prev_position: 52, diff: 7, serp_url: 'https://omegahub.co.uk/sound-bath' },
      competitors: [
        { domain: 'eventbrite.co.uk', position: 3, prev_position: 5, diff: 2 },
        { domain: 'cntraveller.com', position: 8, prev_position: 6, diff: -2 },
      ],
    },
    {
      keyword_id: 13, keyword: 'yoga retreat uk',
      own: { position: null, prev_position: 88, diff: null, serp_url: null },
      competitors: [{ domain: 'cntraveller.com', position: 1, prev_position: 1, diff: 0 }],
    },
  ],
};

/* ===== FETCHERS ============================================================= */

const base = (projectId: string) =>
  `/seo/projects/${encodeURIComponent(projectId)}/competitor-tracking`;

/** GET /weeks — the week selector source (FREE). */
export async function fetchTrackingWeeks(projectId: string): Promise<TrackingWeeks> {
  if (USE_MOCK_DATA) return MOCK_WEEKS;
  const res = await api.get<{ weeks?: unknown[]; latest?: string | null }>(`${base(projectId)}/weeks`);
  return {
    weeks: (res.weeks ?? []).filter((w): w is string => typeof w === 'string' && w !== ''),
    latest: strOrNull(res.latest),
  };
}

/** GET /summary?week — own-domain roll-up for the Overview tab (FREE). */
export async function fetchTrackingSummary(
  projectId: string,
  week?: string,
): Promise<TrackingSummary> {
  if (USE_MOCK_DATA) return MOCK_SUMMARY;
  const res = await api.get<Record<string, unknown>>(`${base(projectId)}/summary`, { week });
  const own = (res.own && typeof res.own === 'object' ? (res.own as Record<string, unknown>) : {});
  return {
    week: strOrNull(res.week),
    prev_week: strOrNull(res.prev_week),
    own_domain: strOrNull(res.own_domain) ?? '—',
    tracked_keywords: numOr0(res.tracked_keywords),
    keywords_captured: numOr0(res.keywords_captured),
    own: { ...mapStatsBase(own), avg_position_diff: numOrNull(own.avg_position_diff) },
    source: strOrNull(res.source) ?? 'dataforseo.serp.organic_live',
    captured_at: strOrNull(res.captured_at),
  };
}

/** GET /competitors?week&limit — competitor table + bubble map (FREE). */
export async function fetchTrackingCompetitors(
  projectId: string,
  week?: string,
  limit = 25,
): Promise<TrackingCompetitors> {
  if (USE_MOCK_DATA) {
    return {
      week: MOCK_SUMMARY.week, prev_week: MOCK_SUMMARY.prev_week,
      total_domains: MOCK_COMPETITORS.length, competitors: MOCK_COMPETITORS.slice(0, limit),
    };
  }
  const res = await api.get<{
    week?: string | null; prev_week?: string | null; total_domains?: number;
    competitors?: Record<string, unknown>[];
  }>(`${base(projectId)}/competitors`, { week, limit });
  return {
    week: strOrNull(res.week),
    prev_week: strOrNull(res.prev_week),
    total_domains: numOr0(res.total_domains),
    competitors: (res.competitors ?? []).map((c) => ({
      ...mapStatsBase(c),
      domain: strOrNull(c.domain) ?? '—',
      is_own: c.is_own === true,
      rank: numOr0(c.rank),
      est_traffic: numOrNull(c.est_traffic),
      position_stddev: numOrNull(c.position_stddev),
    })),
  };
}

/** GET /distribution?weeks&domains — per-domain depth buckets over time (FREE). */
export async function fetchTrackingDistribution(
  projectId: string,
  weeks = 8,
  domains?: string,
): Promise<TrackingDistribution> {
  if (USE_MOCK_DATA) return MOCK_DISTRIBUTION;
  const res = await api.get<{
    weeks?: unknown[]; domains?: Record<string, unknown>[];
  }>(`${base(projectId)}/distribution`, { weeks, domains });
  return {
    weeks: (res.weeks ?? []).filter((w): w is string => typeof w === 'string' && w !== ''),
    domains: (res.domains ?? []).map(
      (d): TrackingDistributionDomain => ({
        domain: strOrNull(d.domain) ?? '—',
        is_own: d.is_own === true,
        series: (Array.isArray(d.series) ? (d.series as Record<string, unknown>[]) : []).map((p) => ({
          week: strOrNull(p.week) ?? '—',
          top3: numOrNull(p.top3),
          top10: numOrNull(p.top10),
          top20: numOrNull(p.top20),
          top100: numOrNull(p.top100),
          visibility_pct: numOrNull(p.visibility_pct),
        })),
      }),
    ),
  };
}

/** GET /keyword-map?week&domain — own + competitor positions per keyword (FREE). */
export async function fetchTrackingKeywordMap(
  projectId: string,
  week?: string,
  domain?: string,
): Promise<TrackingKeywordMap> {
  if (USE_MOCK_DATA) return MOCK_KEYWORD_MAP;
  const res = await api.get<{
    week?: string | null; prev_week?: string | null; keywords?: Record<string, unknown>[];
  }>(`${base(projectId)}/keyword-map`, { week, domain });
  return {
    week: strOrNull(res.week),
    prev_week: strOrNull(res.prev_week),
    keywords: (res.keywords ?? []).map((k): TrackingKeywordMapRow => {
      const own = k.own && typeof k.own === 'object' ? (k.own as Record<string, unknown>) : null;
      return {
        keyword_id: numOr0(k.keyword_id),
        keyword: strOrNull(k.keyword) ?? '—',
        own: own
          ? {
              position: numOrNull(own.position),
              prev_position: numOrNull(own.prev_position),
              diff: numOrNull(own.diff),
              serp_url: strOrNull(own.serp_url),
            }
          : null,
        competitors: (Array.isArray(k.competitors) ? (k.competitors as Record<string, unknown>[]) : []).map(
          (c) => ({
            domain: strOrNull(c.domain) ?? '—',
            position: numOrNull(c.position),
            prev_position: numOrNull(c.prev_position),
            diff: numOrNull(c.diff),
          }),
        ),
      };
    }),
  };
}
